import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { LegalPage } from '@/components/generals/legal-page'

const SECTIONS = [
    {
        id: 'what-we-collect',
        title: 'What we keep',
        body: [
            'When you open an account, we store your name, your email address and a securely hashed form of your password. The password itself is never saved.',
            'When you book a call or write to us, we hold on to what you enter in that form, such as the advisor and time you picked, your topic and any notes you add.',
            'When you browse the site, we measure visits with privacy-friendly analytics that set no cookies and cannot identify you as a person.'
        ]
    },
    {
        id: 'how-we-use-it',
        title: 'Why we use it',
        body: [
            'Your information lets us run your account, set up the calls you book, answer your messages and keep the service safe.',
            'Your data is never sold, never used to show you ads, and never handed to anyone else so they can advertise to you.'
        ]
    },
    {
        id: 'cookies',
        title: 'Our one cookie',
        body: [
            'We place one essential cookie to keep you signed in. It is never used to track you or to show ads, and signing out removes it.'
        ]
    },
    {
        id: 'emails',
        title: 'Email from us',
        body: [
            'We only send email you have asked for, such as a password reset code. These messages can carry a tiny image that shows us whether they were opened, so we can tell they reached you.',
            'Booking requests and contact messages reach our team by email, so a real person can get back to you.'
        ]
    },
    {
        id: 'security',
        title: 'Keeping it safe',
        body: [
            'Your data is encrypted while it travels and while it is stored, only the people helping you can reach it, and sign-in attempts are rate limited to guard your account.'
        ]
    },
    {
        id: 'your-choices',
        title: 'What you control',
        body: [
            'Whenever you like, you can request a copy of your data, ask us to correct it or ask us to delete your account.',
            <>Reach us through the <Link href='/contact' className='text-cream underline decoration-cream/30 underline-offset-4'>contact page</Link> and you will hear back within one business day.</>
        ]
    }
]

const Privacy: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Privacy policy' description='What Verdane keeps, why we use it and how we guard it, plus our one cookie, the emails we send and what you control.' />

            <LegalPage
                eyebrow='Privacy'
                title='Privacy, *without the fog*'
                intro='In short: we gather only what we need to help you, we guard it closely and we will never sell it.'
                updated='October 2026'
                sections={SECTIONS}
            />
        </Layout>
    )
}

export default Privacy
